import type { AppWidgetTab, ExpertsReviewsProps } from '@ecomm/scout'
import type { Locale } from '@ecomm/utils/locale/types'

import { useTranslation } from '@ecomm/i18n'
import type { VideoBannerProps } from '@ecomm/monitoring-components'
import type { LanderBelowTheFoldSchema } from '../components/BelowTheFold/schema'

const findByContentfulId = (
  contentfulId: string,
  images: LanderBelowTheFoldSchema['expertReviewsAssets']['items']
) => {
  /* istanbul ignore next */
  return images.find(element => element.sys.id === contentfulId)
}

export const getContentfulImageId = (key: string, locale: Locale) => {
  const isUSData = locale === 'en-US'
  const imageData: Record<string, string> = {
    Cameras: isUSData ? '4WmsMErxVXBFHEjlFnRcD1' : 'ZYB8WxMtOe454lse5FG2D',
    Controls: isUSData ? '4Z794ao9hfY826K8Tt4FXz' : '6LT6dvk157aAfFdKyeG2Kf',
    Alarms: '4ju8RHivPNhZIeWF0WJ75X',
    Doorbell: isUSData ? '77eZKHHyg4KdFR2T2WhH90' : '38iheGuzSVg8kPQ1aokAnw',
    'us-news': '6uVhRdfLw8y6L3x78SiCuM',
    cnet: '5qECjj8vYgV5ThjxYhWtaw',
    newsweek: '3aPO8RfSjwoD7HLMeS8pQE',
    Alerts: '50wlqz44dQwAzOoxzmPO90', // UK only
    which: '7eH4KhA049wM5ZTOOmWyWd', // UK only
    t3: '5X5OsfxAW6Fc2Zj3HZDlHC', // UK only
    nhw: '2zLbCUSDMAqzV7LqBPgxzE', // UK only
    granddesigns: '3Yk2CSFz84KprPq2t0oyU8' // UK only
  }
  return imageData[key]
}

export const getExpertReviews = (
  images: LanderBelowTheFoldSchema['expertReviewsAssets']['items'],
  locale: Locale,
  reviews: ExpertsReviewsProps['reviews']
) => {
  const reviewsWithImages = reviews.map(review => {
    const imageCTFLId = getContentfulImageId(review.key, locale) ?? ''
    return {
      ...review,
      image: findByContentfulId(imageCTFLId, images)
    }
  })

  return reviewsWithImages.filter(review => !!review.image)
}

export const getAppWidgetData = (
  images: LanderBelowTheFoldSchema['expertReviewsAssets']['items'],
  locale: Locale,
  tabs: readonly AppWidgetTab[]
) =>
  tabs.map(tab => {
    const imageCTFLId = getContentfulImageId(tab.tab, locale) ?? ''
    return {
      ...tab,
      image: findByContentfulId(imageCTFLId, images)
    }
  })

export const useVideoBannerData = (): VideoBannerProps => {
  const { t } = useTranslation('common')

  return {
    text: t('landing-video-banner.text'),
    badge: t('landing-video-banner.badge'),
    title: t('landing-video-banner.title'),
    video: {
      mp4Link: t('landing-video-banner.video.mp4Link'),
      webmLink: t('landing-video-banner.video.webmLink'),
      captionsSrc: t('landing-video-banner.video.captionsSrc'),
      posterImage: t('landing-video-banner.video.posterImage'),
      ariaLabel: t('landing-video-banner.video.ariaLabel')
    },
    postTitle: t('landing-video-banner.postTitle'),
    buttonText: t('landing-video-banner.buttonText'),
    redirectUrl: '/features-alarm-monitoring'
  }
}
